/**
 * Portal Dashboard — redesign Ethereal Green.
 *
 * Métricas de processos + pagamentos do parceiro logado.
 *
 * PR 5 — deixou de ser o pouso do portal (virou o Início, que tem a saudação
 * "Bem-vindo, {nome}"). Esta tela só existe com conteúdo (ver
 * `dashboardTemConteudo` em `lib/portal/dashboardTemConteudo.ts`), então o
 * cabeçalho agora é um título simples — repetir a saudação nas duas telas
 * seria confuso.
 */
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertCircle,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import {
  usePortalDashboard,
  usePortalFinanceiro,
  usePortalProcessos,
} from "@/hooks/usePortalDashboard";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { usePortalPermissions } from "@/hooks/usePortalPermissions";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

// ============================================================================
// Metric Card — paletas de cor por tipo
// ============================================================================

type MetricTone = "emerald" | "amber" | "teal" | "red" | "rose" | "indigo";

const TONES: Record<MetricTone, { bg: string; iconBg: string; iconText: string; accent: string }> =
  {
    emerald: {
      bg: "bg-gradient-to-br from-emerald-50/80 to-white",
      iconBg: "bg-emerald-100",
      iconText: "text-emerald-700",
      accent: "border-emerald-200/60",
    },
    amber: {
      bg: "bg-gradient-to-br from-amber-50/80 to-white",
      iconBg: "bg-amber-100",
      iconText: "text-amber-700",
      accent: "border-amber-200/60",
    },
    teal: {
      bg: "bg-gradient-to-br from-teal-50/80 to-white",
      iconBg: "bg-teal-100",
      iconText: "text-teal-700",
      accent: "border-teal-200/60",
    },
    red: {
      bg: "bg-gradient-to-br from-red-50/80 to-white",
      iconBg: "bg-red-100",
      iconText: "text-red-700",
      accent: "border-red-200/60",
    },
    rose: {
      bg: "bg-gradient-to-br from-rose-50/80 to-white",
      iconBg: "bg-rose-100",
      iconText: "text-rose-700",
      accent: "border-rose-200/60",
    },
    indigo: {
      bg: "bg-gradient-to-br from-indigo-50/80 to-white",
      iconBg: "bg-indigo-100",
      iconText: "text-indigo-700",
      accent: "border-indigo-200/60",
    },
  };

interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  tone: MetricTone;
  isLoading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  description,
  icon,
  tone,
  isLoading,
}) => {
  const palette = TONES[tone];

  if (isLoading) {
    return (
      <Card className={cn("border", palette.accent, palette.bg)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-9 rounded-lg" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 mb-2" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "border transition-all hover:shadow-md hover:-translate-y-0.5",
        palette.accent,
        palette.bg
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-slate-700">{title}</CardTitle>
        <div
          className={cn(
            "h-9 w-9 rounded-lg flex items-center justify-center",
            palette.iconBg,
            palette.iconText
          )}
        >
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Process Status Badge — paleta verde suave
// ============================================================================

const getStatusBadge = (status: string) => {
  const map: Record<string, { className: string; label: string }> = {
    aberto: { className: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Aberto" },
    em_andamento: { className: "bg-teal-100 text-teal-800 border-teal-200", label: "Em Andamento" },
    concluido: { className: "bg-slate-100 text-slate-700 border-slate-200", label: "Concluído" },
    suspenso: { className: "bg-red-100 text-red-800 border-red-200", label: "Suspenso" },
  };
  const config = map[status] || {
    className: "bg-slate-100 text-slate-700 border-slate-200",
    label: status,
  };
  return (
    <Badge variant="outline" className={cn("text-xs font-medium", config.className)}>
      {config.label}
    </Badge>
  );
};

const getPagamentoBadge = (status: string) => {
  const map: Record<string, { className: string; label: string }> = {
    pago: { className: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Pago" },
    pendente: { className: "bg-amber-100 text-amber-800 border-amber-200", label: "Pendente" },
    atrasado: { className: "bg-red-100 text-red-800 border-red-200", label: "Atrasado" },
    cancelado: { className: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelado" },
  };
  const config = map[status] || {
    className: "bg-slate-100 text-slate-700 border-slate-200",
    label: status,
  };
  return (
    <Badge variant="outline" className={cn("text-xs font-medium", config.className)}>
      {config.label}
    </Badge>
  );
};

// ============================================================================
// Main Dashboard Component
// ============================================================================

const PortalDashboard: React.FC = () => {
  const { user } = usePortalAuth();
  const { has } = usePortalPermissions();
  const canSeeFinanceiro = has("portal:financeiro:view");
  // Esconder "Processos" em Administração › Telas do Portal tira esta permissão.
  // Sem a trava aqui, a tela saía do menu e continuava no Início (14/09/2026).
  const canSeeProcessos = has("portal:processos:view");
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = usePortalDashboard();
  const { data: processos, isLoading: processosLoading } = usePortalProcessos(
    { page: 1, page_size: 5 },
    { enabled: canSeeProcessos }
  );
  const { data: financeiro, isLoading: financeiroLoading } = usePortalFinanceiro(
    { page: 1, page_size: 5 },
    { enabled: canSeeFinanceiro } // partner_staff não dispara /portal/financeiro (sem 403)
  );

  const formatCurrency = (value: string | number | undefined | null): string => {
    // Ausência não é zero. O backend OMITE os campos financeiros para quem não
    // tem permissão; mostrar "R$ 0,00" aí seria afirmar um saldo que ninguém
    // apurou. "—" diz a verdade: este número não veio.
    if (value === undefined || value === null) return "—";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return "-";
    }
  };

  if (summaryError) {
    return (
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <Alert variant="destructive" className="bg-red-50/80 border-red-200">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>Erro ao carregar dados do dashboard</span>
            <Button variant="outline" size="sm" onClick={() => refetchSummary()}>
              <RefreshCw className="h-3 w-3 mr-2" />
              Tentar Novamente
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header — PR 5: título simples ("Dashboard"), a saudação mora no Início. */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-emerald-900">Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">
          {user?.substabelecido?.razao_social || "Portal do Parceiro"}
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {canSeeProcessos && (
          <>
            <MetricCard
              title="Processos Ativos"
              value={summary?.processos_ativos ?? 0}
              description="Total de processos não concluídos"
              icon={<FileText className="h-4 w-4" />}
              tone="emerald"
              isLoading={summaryLoading}
            />
            <MetricCard
              title="Pendências Próximas"
              value={summary?.pendencias_proximas ?? 0}
              description="Ações nos próximos 7 dias"
              icon={<AlertCircle className="h-4 w-4" />}
              tone="amber"
              isLoading={summaryLoading}
            />
            <MetricCard
              title="Em Andamento"
              value={summary?.processos_em_andamento ?? 0}
              description="Processos em desenvolvimento"
              icon={<Clock className="h-4 w-4" />}
              tone="teal"
              isLoading={summaryLoading}
            />
          </>
        )}

        {canSeeFinanceiro && (
          <>
            <MetricCard
              title="Saldo Pendente"
              value={formatCurrency(summary?.saldo_pendente)}
              description="Total a receber"
              icon={<DollarSign className="h-4 w-4" />}
              tone="rose"
              isLoading={summaryLoading}
            />
            <MetricCard
              title="Pago Este Mês"
              value={formatCurrency(summary?.total_pago_mes)}
              description="Pagamentos realizados"
              icon={<TrendingUp className="h-4 w-4" />}
              tone="emerald"
              isLoading={summaryLoading}
            />
            <MetricCard
              title="Vencimentos Próximos"
              value={summary?.vencimentos_proximos ?? "—"}
              description="Pagamentos em 7 dias"
              icon={<Calendar className="h-4 w-4" />}
              tone="indigo"
              isLoading={summaryLoading}
            />
          </>
        )}
      </div>

      {/* Recent Processes — gated by portal:processos:view */}
      {canSeeProcessos && (
        <Card className="border-emerald-100/80 bg-white/80 backdrop-blur-sm">
          <CardHeader className="border-b border-emerald-50 pb-4">
            <CardTitle className="text-emerald-900">Processos Recentes</CardTitle>
            <CardDescription>Últimos 5 processos ativos</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {processosLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center p-3 border border-slate-100 rounded-lg"
                  >
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            ) : processos && processos.items.length > 0 ? (
              <div className="space-y-3">
                {processos.items.map((processo) => (
                  <div
                    key={processo.id}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 border border-slate-100 rounded-lg hover:bg-emerald-50/40 hover:border-emerald-200 transition-all"
                  >
                    <div className="space-y-1 flex-1 mb-2 sm:mb-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">
                          {processo.numero}
                        </span>
                        {getStatusBadge(processo.status)}
                      </div>
                      {processo.descricao && (
                        <p className="text-xs text-slate-600 line-clamp-1">{processo.descricao}</p>
                      )}
                      {processo.proxima_acao_prazo && (
                        <p className="text-xs text-slate-500">
                          Próxima ação:{" "}
                          <span className="font-medium">
                            {formatDate(processo.proxima_acao_prazo)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nenhum processo encontrado</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming Payments — gated by portal:financeiro:view */}
      {canSeeFinanceiro && (
        <Card className="border-emerald-100/80 bg-white/80 backdrop-blur-sm">
          <CardHeader className="border-b border-emerald-50 pb-4">
            <CardTitle className="text-emerald-900">Próximos Vencimentos</CardTitle>
            <CardDescription>Últimos 5 pagamentos</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {financeiroLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center p-3 border border-slate-100 rounded-lg"
                  >
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            ) : financeiro && financeiro.items.length > 0 ? (
              <div className="space-y-3">
                {financeiro.items.map((pagamento) => (
                  <div
                    key={pagamento.id}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 border border-slate-100 rounded-lg hover:bg-emerald-50/40 hover:border-emerald-200 transition-all"
                  >
                    <div className="space-y-1 flex-1 mb-2 sm:mb-0">
                      {pagamento.processo_numero && (
                        <span className="text-xs text-slate-500">
                          Processo: <span className="font-medium">{pagamento.processo_numero}</span>
                        </span>
                      )}
                      {pagamento.descricao && (
                        <p className="text-sm font-medium line-clamp-1 text-slate-800">
                          {pagamento.descricao}
                        </p>
                      )}
                      <p className="text-xs text-slate-600">
                        Vencimento:{" "}
                        <span className="font-medium">{formatDate(pagamento.data_vencimento)}</span>
                      </p>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-base font-bold text-emerald-900">
                        {formatCurrency(pagamento.valor)}
                      </p>
                      {getPagamentoBadge(pagamento.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nenhum pagamento encontrado</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortalDashboard;
